import { z } from 'zod';
import type { ProjectSnapshot } from '../types';

/**
 * Zod schema for VersionChange — matches the VersionChange interface in types/index.ts.
 * All fields validated structurally. timestamp is coerced from ISO string to Date
 * since JSON serialisation strips the Date constructor.
 */
const VersionChangeSchema = z.object({
  id: z.string().optional(),
  type: z.enum(["create", "update", "delete", "move"]),
  target: z.enum(["component", "page", "navigation", "settings"]),
  targetId: z.string(),
  pageId: z.string().optional(),
  field: z.string().optional(),
  oldValue: z.unknown().optional(),
  newValue: z.unknown().optional(),
  userId: z.string(),
  timestamp: z.coerce.date(),
  description: z.string().optional(),
});

/**
 * Zod schema for ProjectVersion — validates all required top-level fields.
 *
 * snapshot: Full nested validation (ProjectSnapshot → PageSnapshot[] → Component[]) is
 * deferred as a follow-up. z.custom<ProjectSnapshot>() accepts any value at runtime but
 * types it as ProjectSnapshot so that callers receive a fully-typed ProjectVersion without
 * a cast. Do NOT use z.any() here — that would defeat the boundary parse.
 * Follow-up: build a complete ProjectSnapshotSchema once Component schema coverage exists.
 *
 * createdAt: coerced from ISO string since JSON transport strips the Date constructor.
 */
export const ProjectVersionSchema = z.object({
  id: z.string(),
  version: z.number(),
  name: z.string().optional(),
  description: z.string().optional(),
  createdBy: z.string(),
  createdAt: z.coerce.date(),
  // Snapshot validation deferred — see note above.
  snapshot: z.custom<ProjectSnapshot>(),
  changes: z.array(VersionChangeSchema),
  tags: z.array(z.string()),
  isPublished: z.boolean(),
});

export type ProjectVersionParsed = z.infer<typeof ProjectVersionSchema>;
